import { useEffect, useState } from "react";
import db, { createRef } from "src/db";

type Option = { label: string; value: string };
type Account = Record<string, any> & { id: string; name: string; type: string };
type FilterState = {
  clientId?: string;
  dataAccount: Account[];
  filterAccount: Option[];
  filterShipment: Option[];
  accountsLoaded: boolean;
  shipmentsLoaded: boolean;
  error?: string;
};

const emptyState = (clientId?: string): FilterState => ({
  clientId, dataAccount: [], filterAccount: [], filterShipment: [],
  accountsLoaded: false, shipmentsLoaded: false,
});

export function useOrderListFilters(clientId?: string) {
  const [state, setState] = useState<FilterState>(() => emptyState(clientId));

  useEffect(() => {
    let cancelled = false;
    setState(emptyState(clientId));
    if (!clientId) return;

    const update = (patch: Partial<FilterState>) => {
      if (!cancelled) setState(previous => ({ ...previous, ...patch }));
    };
    const loadAccounts = async () => {
      try {
        // Reuse the existing client ASC, type ASC composite; sort names locally.
        const snapshot = await db.collection("account")
          .where("client", "==", createRef("clients", clientId))
          .where("type", "!=", "whatsapp")
          .orderBy("type", "asc")
          .get();
        const accounts: Account[] = [];
        snapshot.forEach(doc => {
          const data = doc.data();
          accounts.push({ ...data, id: doc.id, name: data.name ?? "", type: data.type ?? "" });
        });
        accounts.sort((a, b) => String(a.type ?? "").localeCompare(String(b.type ?? "")) ||
          String(a.name ?? "").localeCompare(String(b.name ?? "")));
        update({
          dataAccount: accounts,
          filterAccount: accounts.map(account => ({
            label: `${account.type} - ${account.name}`,
            value: `id_${account.id}`,
          })),
        });
      } catch (error) {
        console.error("Error loading order account filters:", error);
        update({ error: "Unable to load order filters. Please refresh and try again." });
      } finally {
        update({ accountsLoaded: true });
      }
    };
    const loadShipments = async () => {
      try {
        const snapshot = await db.collection("shipments")
          .where("isParent", "==", true)
          .orderBy("name", "asc")
          .get();
        const options: Option[] = [];
        snapshot.forEach(doc => options.push({ label: doc.data().name, value: doc.data().name }));
        update({ filterShipment: options });
      } catch (error) {
        console.error("Error loading order shipment filters:", error);
        update({ error: "Unable to load order filters. Please refresh and try again." });
      } finally {
        update({ shipmentsLoaded: true });
      }
    };
    void loadAccounts();
    void loadShipments();
    return () => { cancelled = true; };
  }, [clientId]);

  // Never expose the previous client's accounts during a client transition.
  const current = state.clientId === clientId ? state : emptyState(clientId);
  return {
    dataAccount: current.dataAccount,
    filterAccount: current.filterAccount,
    filterShipment: current.filterShipment,
    loading: Boolean(clientId) && (!current.accountsLoaded || !current.shipmentsLoaded),
    error: current.error,
  };
}
